'use client';

import { ArrowLeft } from 'lucide-react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function DpaPage() {
  return (
    <div className="min-h-screen">
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.13]"></div>
      </div>

      <Header />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-neutral-400 hover:text-white transition-colors group mb-8 cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          <span className="text-sm font-medium">Back</span>
        </Link>

        <article className="prose prose-invert prose-neutral max-w-none">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-neutral-400 bg-clip-text text-transparent mb-4">
            Data Processing Addendum
          </h1>
          <p className="text-neutral-400 text-sm mb-8">Effective date: June 2026 - summary</p>

          <section className="mb-8">
            <p className="text-neutral-300 leading-relaxed">
              This page summarizes the key terms of the Silker AI Data Processing Addendum (DPA). It is provided for transparency and does not replace a signed agreement. For a countersigned DPA, see the contact details at the end of this page.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">1. Roles</h2>
            <p className="text-neutral-300 leading-relaxed">
              In relation to personal data processed through the service, the <strong className="text-white">customer is the data controller</strong> and <strong className="text-white">Silker AI is the data processor</strong>. Silker AI processes personal data only on the customer&apos;s documented instructions.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">2. Processing Scope</h2>
            <p className="text-neutral-300 leading-relaxed mb-4">
              Silker AI processes security telemetry generated by the SDK or proxy to detect and report threats. This includes HTTP request metadata, network identifiers (IP, user agent), and request context for flagged traffic, with PII sanitized client-side before transmission.
            </p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
              <li><strong className="text-white">Subject matter:</strong> runtime threat detection and security analytics</li>
              <li><strong className="text-white">Duration:</strong> for the term of the subscription, subject to plan retention windows</li>
              <li><strong className="text-white">Nature &amp; purpose:</strong> detecting, classifying, and reporting security threats</li>
              <li><strong className="text-white">Data subjects:</strong> the customer&apos;s end users and visitors</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">3. Subprocessors</h2>
            <p className="text-neutral-300 leading-relaxed mb-4">
              Silker AI engages the following subprocessors to deliver the service:
            </p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
              <li><strong className="text-white">Cloud hosting:</strong> EU-hosted infrastructure for storage and processing</li>
              <li><strong className="text-white">Stripe:</strong> billing and payment processing</li>
              <li><strong className="text-white">OpenAI:</strong> AI analysis of flagged threats</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">4. Security Measures</h2>
            <p className="text-neutral-300 leading-relaxed">
              Silker AI maintains appropriate technical and organizational measures, including API keys stored as SHA-256 hashes, row-level security on tenant data, TLS in transit, client-side PII sanitization, EU hosting, and least-privilege access. See our{' '}
              <Link href="/security" className="text-rose-400 hover:text-rose-300 transition">Security</Link>{' '}page for details.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">5. Data Subject Requests</h2>
            <p className="text-neutral-300 leading-relaxed">
              Silker AI assists the customer, taking into account the nature of processing, in responding to data subject requests (such as access or deletion). Requests can be facilitated via support at{' '}
              <a href="mailto:privacy@silkerai.com" className="text-rose-400 hover:text-rose-300 transition">privacy@silkerai.com</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">6. Deletion on Termination</h2>
            <p className="text-neutral-300 leading-relaxed">
              Upon termination of the service, Silker AI will delete or return customer personal data in accordance with the agreement, subject to any retention required by applicable law.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">Contact us for a signed DPA</h2>
            <p className="text-neutral-300 leading-relaxed">
              Need a countersigned DPA for your compliance records? Reach out to{' '}
              <a href="mailto:privacy@silkerai.com?subject=Signed%20DPA%20request" className="text-rose-400 hover:text-rose-300 transition">
                privacy@silkerai.com
              </a>.
            </p>
          </section>
        </article>
      </main>

      <Footer />
    </div>
  );
}
